import { onBeforeUnmount, ref } from "vue";

import { articleStatus, planArticle, writeArticle } from "../api/articles";
import { getDeliverable, publishDeliverable } from "../api/deliverables";

export const ART_POLL_MS = 10000;
export const ART_POLL_MAX = 180;
export const ART_POLL_ERRMAX = 5;
export const ART_PLAN_JOB_KEY = "soa_art_plan_job";
export const ART_JOB_KEY = "soa_art_job";
export const MC_ART_AGAIN = /(再试一次|再试|再来一次|再写一次|重写|再生成一次|重新写|继续写|接着写|try again|retry|write it again|rewrite it|do it again|run it again|もう一度|やり直)/i;

export function planToText(plan, t) {
  if (!plan) return "";
  if (typeof plan === "string") return plan;
  const sections = [
    [t("art_plan_title_f"), plan.title],
    [t("art_plan_audience"), plan.audience],
    [t("art_plan_points"), plan.key_points],
    [t("art_plan_outline"), plan.outline],
    [t("art_plan_cites"), plan.citations],
  ];
  function line(value) {
    return Array.isArray(value) ? value.map((item) => `- ${item}`).join("\n") : String(value);
  }
  return sections
    .filter(([, value]) => value && (!Array.isArray(value) || value.length))
    .map(([label, value]) => (Array.isArray(value) ? `${label}:\n${line(value)}` : `${label}: ${line(value)}`))
    .join("\n\n");
}

function readJson(key) {
  try { return JSON.parse(sessionStorage.getItem(key) || "null"); } catch { return null; }
}
function writeJson(key, value) {
  try { sessionStorage.setItem(key, JSON.stringify(value)); } catch { /* optional storage */ }
}
function removeKey(key) {
  try { sessionStorage.removeItem(key); } catch { /* optional storage */ }
}

function sleep(ms, alive) {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(resolve, ms);
    const abort = () => { clearTimeout(timer); reject(new DOMException("Aborted", "AbortError")); };
    if (alive?.aborted) { abort(); return; }
    alive?.addEventListener("abort", abort, { once: true });
  });
}

export function useMayaArticle({
  t,
  lang,
  currentBrand,
  route,
  selectBrandByName,
  say,
  pushMessage,
  upgradeBubble,
  send,
  pushCard,
  mdLite,
  toast,
}) {
  const stage = ref("");
  const buildTitle = ref("");
  const planText = ref("");
  const draft = ref(null);
  const ctx = ref({});
  const busy = ref(false);
  const unconf = ref(null);
  const pubLine = ref("");
  const copied = ref(false);
  const retry = ref(null);
  let copyTimer = 0;
  let alive = new AbortController();

  function stopPoll() {
    alive.abort();
    alive = new AbortController();
  }
  onBeforeUnmount(() => { stopPoll(); clearTimeout(copyTimer); });

  function activeBrand() {
    return ctx.value.brand || currentBrand.value?.brand || "";
  }
  function contextFromQuery() {
    const query = route.query || {};
    return {
      brand: String(query.article_brand || currentBrand.value?.brand || ""),
      q: String(query.article_q || ""),
      cn: String(query.article_cn || ""),
      layer: String(query.article_layer || ""),
      type: String(query.article_type || ""),
      vol: Number(query.article_vol) || 0,
      rec: query.article_rec ?? "",
      score: Number(query.article_score) || 0,
      lang: lang(),
    };
  }
  function questionOf(context) {
    return lang() === "zh" && context.cn ? context.cn : context.q;
  }
  function idle() {
    busy.value = false;
    if (stage.value === "build") stage.value = "";
  }
  function showBuild(title) {
    stage.value = "build";
    buildTitle.value = title;
    unconf.value = null;
    pubLine.value = "";
  }

  async function pollJob(job, { expect } = {}) {
    let tries = 0;
    let errs = 0;
    const signal = alive.signal;
    while (tries < ART_POLL_MAX) {
      tries += 1;
      try {
        const status = await articleStatus(job, { signal });
        if (expect === "plan") {
          if (status.state === "done" && status.plan) return status;
          if (status.state === "failed") return status;
          if (status.state === "running") errs = 0;
          else errs += 1;
        } else {
          if (status.state === "done" && status.slug) return status;
          if (status.state === "failed") return status;
          if (status.state === "running") errs = 0;
          else errs += 1;
        }
      } catch (caught) {
        if (caught.name === "AbortError") throw caught;
        errs += 1;
      }
      if (errs >= ART_POLL_ERRMAX) return {};
      await sleep(ART_POLL_MS, signal);
    }
    return {};
  }

  function failBad(msg) {
    retry.value = null;
    idle();
    say(msg || t("maya_art_plan_fail"));
  }
  function failRetry(kind, context, text) {
    idle();
    const message = pushMessage({
      role: "assistant",
      content: t("maya_art_fail_retry"),
      action: {
        type: "acts",
        acts: [{
          kind: "cta",
          label: kind === "plan" ? t("maya_art_retry_plan") : t("maya_art_retry_write"),
          retryArticle: { kind, ctx: context, plan: text || "" },
        }],
      },
    });
    retry.value = { kind, ctx: context, plan: text || "", message };
  }
  function handleGate(payload) {
    if (payload?.upgrade) {
      idle();
      upgradeBubble(payload.msg, payload.url);
      return true;
    }
    if (payload?.reason === "bad_topic") {
      failBad(payload.msg);
      return true;
    }
    return false;
  }

  async function articlePlan(context) {
    const next = { ...context, lang: lang(), brand: context.brand || activeBrand() };
    ctx.value = next;
    if (next.brand && selectBrandByName) selectBrandByName(next.brand, { silent: true, keepCurrent: true });
    retry.value = null;
    say(t("maya_art_plan_msg", { q: questionOf(next) }));
    showBuild(t("maya_art_plan_working"));
    busy.value = true;
    try {
      const response = await planArticle({
        brand: next.brand,
        q: next.q,
        cn: next.cn,
        layer: next.layer,
        type: next.type,
        vol: next.vol,
        rec: next.rec,
        score: next.score,
        lang: next.lang,
      });
      if (response?.job && !response.done) {
        writeJson(ART_PLAN_JOB_KEY, { job: response.job, ctx: next, ts: Date.now() });
        const finished = await pollJob(response.job, { expect: "plan" });
        removeKey(ART_PLAN_JOB_KEY);
        finishPlan(next, finished);
        return;
      }
      finishPlan(next, response);
    } catch (caught) {
      if (caught.name === "AbortError") return;
      removeKey(ART_PLAN_JOB_KEY);
      failRetry("plan", next, "");
    }
  }

  function finishPlan(context, payload) {
    idle();
    if (handleGate(payload)) return;
    if (!(payload && payload.ok && payload.plan)) { failRetry("plan", context, ""); return; }
    ctx.value = context;
    planText.value = planToText(payload.plan, t);
    draft.value = payload.plan;
    stage.value = "plan";
  }

  async function articleWrite(context, text) {
    const next = { ...context, lang: lang(), brand: context.brand || activeBrand() };
    ctx.value = next;
    retry.value = null;
    say(t("maya_art_write_msg"));
    showBuild(t("maya_art_write_working"));
    busy.value = true;
    try {
      const response = await writeArticle({
        brand: next.brand,
        q: next.q,
        cn: next.cn,
        layer: next.layer,
        vol: next.vol,
        rec: next.rec,
        plan: text,
        lang: next.lang,
      });
      if (handleGate(response)) return;
      if (response?.ok && response.done && response.slug) {
        idle();
        renderArticle(next, response);
        return;
      }
      if (!(response?.ok && response.job)) { failRetry("write", next, text); return; }
      writeJson(ART_JOB_KEY, { job: response.job, ctx: next, plan: text || "", ts: Date.now() });
      const finished = await pollJob(response.job, { expect: "article" });
      removeKey(ART_JOB_KEY);
      if (finished?.slug) { idle(); renderArticle(next, finished); return; }
      failRetry("write", next, text);
    } catch (caught) {
      if (caught.name === "AbortError") return;
      removeKey(ART_JOB_KEY);
      failRetry("write", next, text);
    }
  }

  function renderArticle(context, payload) {
    ctx.value = context;
    draft.value = payload;
    planText.value = "";
    stage.value = "article";
    unconf.value = null;
    pubLine.value = "";
    busy.value = false;
  }

  function writePlannedArticle() {
    return articleWrite(ctx.value, planText.value);
  }
  function regeneratePlan() {
    return articlePlan(ctx.value);
  }

  async function publishArticle(force = false) {
    const article = draft.value;
    if (!article?.slug || busy.value) return;
    const wasPub = Boolean(article.published);
    const label = wasPub ? t("art_republish") : t("art_publish");
    busy.value = true;
    try {
      const result = await publishDeliverable(article.slug, force);
      if (result?.ok) {
        article.published = true;
        article.published_url = result.url;
        draft.value = { ...article };
        pubLine.value = result.url || "";
        unconf.value = null;
        if (result.card) pushCard?.(result.card);
        toast?.(wasPub ? t("art_republished") : t("art_live_ok"), result.url || "", "ok");
      } else if (result?.upgrade) {
        upgradeBubble(result.msg, result.url);
      } else if (result?.error === "unconfirmed") {
        unconf.value = result;
        pubLine.value = "";
      } else {
        const msg = result?.msg || result?.error || t("maya_art_write_fail");
        toast?.(msg, "", "warn");
        if (result?.need) say(msg);
      }
      void label;
    } catch (caught) {
      toast?.(caught.message || t("maya_art_write_fail"), "", "warn");
    } finally {
      busy.value = false;
    }
  }

  function copyBody() {
    const text = draft.value?.body || "";
    if (!text) return;
    navigator.clipboard?.writeText(text).then(() => {
      copied.value = true;
      clearTimeout(copyTimer);
      copyTimer = setTimeout(() => { copied.value = false; }, 1400);
    }).catch(() => { copied.value = false; });
  }

  function askToFill() {
    const article = draft.value || {};
    const n = String(unconf.value?.needs_confirm || 0);
    send(t("art_unconf_say", { n, t: article.title || article.slug || "", s: article.slug || "" }));
  }

  async function openDeliverable(slug) {
    if (!slug) return false;
    busy.value = true;
    showBuild(t("wb_loading"));
    try {
      const data = await getDeliverable(slug);
      if (data?.slug) {
        renderArticle({ brand: data.brand || activeBrand(), q: data.title || "" }, data);
        return true;
      }
      idle();
      say(t("wb_notfound"));
    } catch {
      idle();
      say(t("wb_notfound"));
    }
    return false;
  }

  function tryTypedRetry(text) {
    const pending = retry.value;
    if (!pending) return false;
    retry.value = null;
    if (!MC_ART_AGAIN.test((text || "").trim())) return false;
    if (pending.kind === "plan") articlePlan(pending.ctx);
    else articleWrite(pending.ctx, pending.plan);
    return true;
  }

  function runRetry(chip) {
    const action = chip?.retryArticle;
    if (!action) return false;
    retry.value = null;
    if (action.kind === "plan") articlePlan(action.ctx);
    else articleWrite(action.ctx, action.plan);
    return true;
  }

  async function startFromQuery() {
    if (route.query.article_brand && selectBrandByName) {
      selectBrandByName(String(route.query.article_brand), { silent: true, keepCurrent: true });
    }
    if (route.query.article_edit) return openDeliverable(String(route.query.article_edit));
    if (route.query.article_q) {
      await articlePlan(contextFromQuery());
      return true;
    }
    return resumeJobs();
  }

  function resumeJobs() {
    const planJob = readJson(ART_PLAN_JOB_KEY);
    if (planJob?.job && Date.now() - (planJob.ts || 0) < 2 * 60 * 60 * 1000) {
      ctx.value = planJob.ctx || contextFromQuery();
      showBuild(t("maya_art_plan_working"));
      busy.value = true;
      pollJob(planJob.job, { expect: "plan" }).then((finished) => {
        removeKey(ART_PLAN_JOB_KEY);
        finishPlan(ctx.value, finished);
      }).catch((caught) => {
        if (caught.name !== "AbortError") failRetry("plan", ctx.value, "");
      });
      return true;
    }
    const writeJob = readJson(ART_JOB_KEY);
    if (writeJob?.job && Date.now() - (writeJob.ts || 0) < 10 * 60 * 1000) {
      ctx.value = writeJob.ctx || contextFromQuery();
      showBuild(t("maya_art_write_working"));
      busy.value = true;
      pollJob(writeJob.job, { expect: "article" }).then((finished) => {
        removeKey(ART_JOB_KEY);
        if (finished?.slug) { idle(); renderArticle(ctx.value, finished); }
        else failRetry("write", ctx.value, writeJob.plan || "");
      }).catch((caught) => {
        if (caught.name !== "AbortError") failRetry("write", ctx.value, writeJob.plan || "");
      });
      return true;
    }
    return false;
  }

  function bodyHtml() {
    return draft.value?.body ? mdLite(draft.value.body) : "";
  }

  return {
    stage,
    buildTitle,
    planText,
    draft,
    ctx,
    busy,
    unconf,
    pubLine,
    copied,
    articlePlan,
    writePlannedArticle,
    regeneratePlan,
    publishArticle,
    copyBody,
    askToFill,
    startFromQuery,
    tryTypedRetry,
    runRetry,
    bodyHtml,
    stopPoll,
  };
}
